import React from 'react';
import { cn } from 'lib/utils';

export interface FieldProps {
    label?: React.ReactNode;
    /** 입력 아래 보조 설명 */
    hint?: React.ReactNode;
    error?: string;
    required?: boolean;
    htmlFor?: string;
    className?: string;
    children: React.ReactNode;
}

/**
 * 모든 입력의 공통 껍데기 — 라벨 / 입력 / 보조문구·오류를 한 가지 리듬으로 묶는다.
 * 라벨 12px, 입력 높이 36px, 보조문구 11px (docs/DESIGN.md §3)
 */
const Field: React.FC<FieldProps> = ({
    label,
    hint,
    error,
    required,
    htmlFor,
    className = '',
    children,
}) => (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
        {label && (
            <label htmlFor={htmlFor} className="text-sm font-medium text-neutral-700">
                {label}
                {required && <span className="ml-0.5 text-destructive">*</span>}
            </label>
        )}
        {children}
        {error ? (
            <span className="text-xs text-destructive">{error}</span>
        ) : hint ? (
            <span className="text-xs text-muted-foreground">{hint}</span>
        ) : null}
    </div>
);

export default Field;

/** 입력과 같은 높이·테두리를 쓰는 읽기 전용 값 */
export const ReadonlyField: React.FC<{
    label?: React.ReactNode;
    hint?: React.ReactNode;
    className?: string;
    children: React.ReactNode;
}> = ({ label, hint, className = '', children }) => (
    <Field label={label} hint={hint} className={className}>
        <div className="flex h-9 items-center rounded border border-border bg-neutral-50 px-3 text-base text-foreground">
            {children}
        </div>
    </Field>
);

/** 입력 공통 클래스 — 직접 input을 쓸 때도 이 값을 쓴다 */
export const controlClass = cn(
    'h-9 w-full min-w-0 rounded border border-input bg-background px-3 text-base text-foreground',
    'transition-colors duration-fast placeholder:text-neutral-400',
    'focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring',
    'disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400',
    'aria-[invalid=true]:border-destructive'
);
